import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'cricket_token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Auth supports two flows simultaneously during the v1 -> v2 migration:
 *
 *   v2 (preferred): phone OTP or email magic link, returns an "account"
 *                   JWT. Used by the new OnboardingFlow.
 *   v1 (legacy):    email + password against /auth/login. Returns a
 *                   "user" payload. Existing protected pages keep working.
 *
 * The `identity` field is the v2 identity blob (account + household_id +
 * onboarding_complete). The `user` field is preserved for legacy pages.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));

  const setToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setTokenState(newToken);
  }, []);

  const hydrate = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };

    // Try v2 first — cheap, doesn't error on legacy tokens, just 401s.
    try {
      const v2 = await axios.get(`${API}/auth/v2/me`, { headers });
      setIdentity(v2.data);
      setUser({
        id: v2.data.account.id,
        full_name: v2.data.account.full_name,
        email: v2.data.account.email,
        cricket_role: v2.data.account.role,
        ui_flags: v2.data.ui_flags || {},
        identity_version: 'v2',
      });
      setLoading(false);
      return;
    } catch (err) {
      console.warn('Auth hydrate failed (v2 endpoint):', err.response?.status);
    }

    // Legacy /auth/me
    try {
      const legacy = await axios.get(`${API}/auth/me`, { headers });
      setUser({ ...legacy.data, identity_version: 'v1' });
    } catch (err) {
      console.warn('Auth hydrate failed (legacy endpoint), clearing token:', err.response?.status);
      setToken(null);
      setUser(null);
      setIdentity(null);
    } finally {
      setLoading(false);
    }
  }, [token, setToken]);

  useEffect(() => { hydrate(); }, [hydrate]);

  // ---------- v2 phone OTP ----------

  const requestOtp = async (phone) => {
    try {
      const res = await axios.post(`${API}/auth/v2/otp/request`, { phone });
      return { success: true, ...res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'otp_request_failed' };
    }
  };

  const verifyOtp = async (phone, code) => {
    try {
      const res = await axios.post(`${API}/auth/v2/otp/verify`, { phone, code });
      setToken(res.data.token);
      setIdentity({
        account: res.data.account,
        household_id: res.data.household_id,
        onboarding_complete: res.data.onboarding_complete,
      });
      setUser({
        id: res.data.account.id,
        full_name: res.data.account.full_name,
        email: res.data.account.email,
        cricket_role: res.data.account.role,
        identity_version: 'v2',
      });
      return {
        success: true,
        onboarding_complete: res.data.onboarding_complete,
      };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'verify_failed' };
    }
  };

  const completeOnboarding = async (payload) => {
    try {
      // Forward a stashed coach-invitation token (from /i/<token>) so
      // the backend can auto-link the coach on first onboarding.
      let body = payload;
      try {
        const inviteToken = localStorage.getItem('cricket_invitation_token');
        if (inviteToken && !payload.invitation_token) {
          body = { ...payload, invitation_token: inviteToken };
        }
      } catch (err) { console.warn('Could not read stashed invitation token:', err); }
      const res = await axios.post(
        `${API}/auth/v2/onboarding/complete`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Clear the stashed token; it's single-use.
      try { localStorage.removeItem('cricket_invitation_token'); } catch (err) { console.warn('Could not clear invitation token:', err); }
      setToken(res.data.token);
      setIdentity((prev) => ({
        ...(prev || {}),
        account: res.data.account,
        household_id: res.data.household_id,
        onboarding_complete: true,
      }));
      setUser({
        id: res.data.account.id,
        full_name: res.data.account.full_name,
        email: res.data.account.email,
        cricket_role: res.data.account.role,
        identity_version: 'v2',
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'onboarding_failed' };
    }
  };

  // ---------- legacy email/password (kept for migration) ----------

  const login = async (email, password) => {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);
      const res = await axios.post(`${API}/auth/login`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setToken(res.data.access_token);
      setUser({ ...res.data.user, identity_version: 'v1' });
      setLoading(false);
      return { success: true };
    } catch (err) {
      setLoading(false);
      return { success: false, error: err.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, fullName, cricketRole = 'Player', cricclubsId = null) => {
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email, password, full_name: fullName,
        cricket_role: cricketRole, cricclubs_id: cricclubsId,
      });
      setToken(res.data.access_token);
      setUser({ ...res.data.user, identity_version: 'v1' });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIdentity(null);
  };

  const setUiFlag = async (key, value = true) => {
    if (!token) return;
    try {
      const res = await axios.post(
        `${API}/auth/v2/me/ui-flags`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const flags = res.data?.ui_flags || {};
      setUser((prev) => prev ? { ...prev, ui_flags: flags } : prev);
      return flags;
    } catch (err) { console.warn('UI flag update failed (non-blocking):', err); }
  };

  const getAuthHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  return (
    <AuthContext.Provider value={{
      user,
      identity,
      loading,
      isAuthenticated: !!user,
      onboardingComplete: identity ? identity.onboarding_complete : true,
      // v2
      requestOtp,
      verifyOtp,
      completeOnboarding,
      // legacy
      login,
      register,
      // common
      logout,
      getAuthHeader,
      setUiFlag,
      token,
      refresh: hydrate,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
